import { filterClasses } from "@swivl-mono/helpers";
import { useEffect, useState } from "react";

const ATTEMPTS_STORAGE_KEY = "swivl.eval.attemptsPerCase";

/**
 * Read the persisted "attempts per case" preference. Used by every place
 * in the studio that starts an eval run — the new-run panel, the user-
 * stories page bulk runner, single-story re-runs — so the operator's
 * preference is consistent regardless of which surface they kick the run
 * from. Defaults to 1 (legacy single-pass).
 */
export function readAttemptsPerCase(): number {
  if (typeof window === "undefined") return 1;
  const raw = window.localStorage.getItem(ATTEMPTS_STORAGE_KEY);
  if (!raw) return 1;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(10, n);
}

export function writeAttemptsPerCase(n: number): void {
  if (typeof window === "undefined") return;
  const clamped = Math.max(1, Math.min(10, Math.floor(n)));
  window.localStorage.setItem(ATTEMPTS_STORAGE_KEY, String(clamped));
  // Notify other mounted instances of this widget so they re-read.
  window.dispatchEvent(new CustomEvent("swivl:eval-attempts-changed", { detail: clamped }));
}

interface AttemptsSettingProps {
  /** "compact" — used in headers/toolbars (label + small input + presets, single line).
   *  "inline" — used in the run panel footer (currently the same as compact). */
  variant?: "compact" | "inline";
  /** Hide the helper text underneath. Default: shown in compact. */
  hideHelp?: boolean;
  /** Optional change callback. The widget always writes to localStorage; this
   *  fires after the write so the host can also pick up the value if needed. */
  onChange?: (n: number) => void;
}

/**
 * Editable "attempts per case" widget. Persists to localStorage so the
 * setting follows the operator across surfaces (new-run panel, user
 * stories page header, anywhere else we drop it). Cross-tab/cross-mount
 * sync via a custom `swivl:eval-attempts-changed` window event.
 */
export function AttemptsSetting({ variant = "compact", hideHelp = false, onChange }: AttemptsSettingProps): React.ReactElement {
  const [value, setValue] = useState<number>(() => readAttemptsPerCase());

  // Subscribe to the custom event so multiple mounted instances stay in sync.
  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail === "number") setValue(detail);
    };
    window.addEventListener("swivl:eval-attempts-changed", handler);
    return () => window.removeEventListener("swivl:eval-attempts-changed", handler);
  }, []);

  const set = (n: number): void => {
    const clamped = Math.max(1, Math.min(10, Math.floor(n)));
    setValue(clamped);
    writeAttemptsPerCase(clamped);
    onChange?.(clamped);
  };

  return (
    <div className={filterClasses("flex flex-col gap-1 text-sm", variant === "compact" ? "" : "")}>
      <div className="flex items-center gap-2">
        <label htmlFor="attempts-per-case" className="text-grey-500">
          Attempts per case
        </label>
        <input
          id="attempts-per-case"
          type="number"
          min={1}
          max={10}
          value={value}
          onChange={(e) => set(parseInt(e.target.value || "1", 10))}
          className="w-14 rounded border border-grey-200 px-2 py-1 text-center"
          title="Each case runs this many times. Pass = any attempt passes. Use 1 for fast iteration, 3+ to verify a run is reliably green."
        />
        <div className="flex gap-0.5">
          {[1, 3, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => set(n)}
              className={filterClasses(
                "rounded px-1.5 py-0.5 text-xs",
                value === n ? "bg-orange-500 text-white" : "bg-grey-100 text-grey-600 hover:bg-grey-200"
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      {!hideHelp && (
        <p className="text-[11px] text-grey-400">
          Each case runs this many times — pass = any attempt passes. Saved in your browser; applies to runs started anywhere in the studio.
        </p>
      )}
    </div>
  );
}
